import { EditorSelection } from "@codemirror/state";
import { syntaxTree } from "@codemirror/language";

import { ancestorOfNodeWithType } from "../utils/tree-operations/ancestors.js";

export const findCurrentSectionHeadingLevel = (state) => {
    const selections = state.selection.ranges.map((range) => rangeInfo(state, range));
    const currentLevels = new Set(selections.map((item) => item.level));

    return currentLevels.size === 1 ? selections[0] : null;
};

export const rangeInfo = (state, range) => {
    const tree = syntaxTree(state);

    const fromNode = tree.resolveInner(range.from, 1);
    const fromAncestor = ancestorOfNodeWithType(fromNode, "SectioningCommand");

    const toNode = tree.resolveInner(range.to, -1);
    const toAncestor = ancestorOfNodeWithType(toNode, "SectioningCommand");

    const command = fromAncestor ?? toAncestor;

    if (!command) {
        return { range, level: "text" };
    }

    if (fromAncestor && toAncestor) {
        if (fromAncestor !== toAncestor) {
            return { range, level: "text" };
        }
    } else if (!range.empty) {
        return { range, level: "text" };
    }

    const ctrlSeq = command.firstChild;
    if (!ctrlSeq) {
        return { range, level: "text" };
    }

    const level = state.sliceDoc(ctrlSeq.from + 1, ctrlSeq.to).trim();

    return { command, ctrlSeq, level, range };
};

export const setSectionHeadingLevel = (view, level) => {
    view.dispatch(
        view.state.changeByRange((range) => {
            const info = rangeInfo(view.state, range);

            if (level === info.level) {
                return { range };
            }

            if (level === "text" && info.command) {
                const argument = info.command.getChild("SectioningArgument");
                if (!argument) {
                    return { range };
                }

                const content = view.state.sliceDoc(argument.from + 1, argument.to - 1);
                const changedRange = range.map(
                    view.state.changes([
                        {
                            from: info.command.from,
                            to: argument.from + 1,
                            insert: "",
                        },
                    ]),
                    1,
                );

                return {
                    range: changedRange,
                    changes: [
                        {
                            from: info.command.from,
                            to: info.command.to,
                            insert: content,
                        },
                    ],
                };
            }

            if (info.level === "text") {
                const insert = {
                    prefix: `\\${level}{`,
                    suffix: "}",
                };

                const originalRange = range;
                const line = view.state.doc.lineAt(range.anchor);
                if (range.empty) {
                    range = EditorSelection.range(line.from, line.to);
                } else {
                    if (range.from !== line.from) {
                        insert.prefix = `\n${insert.prefix}`;
                    }

                    if (range.to !== line.to) {
                        insert.suffix += "\n";
                    }
                }

                const content = view.state.sliceDoc(range.from, range.to);
                const changedRange = originalRange.map(
                    view.state.changes([
                        {
                            from: range.from,
                            insert: insert.prefix,
                        },
                    ]),
                    1,
                );

                return {
                    range: changedRange,
                    changes: [
                        {
                            from: range.from,
                            to: range.to,
                            insert: `${insert.prefix}${content}${insert.suffix}`,
                        },
                    ],
                };
            }

            if (!info.ctrlSeq) {
                return { range };
            }

            const changes = view.state.changes([
                {
                    from: info.ctrlSeq.from + 1,
                    to: info.ctrlSeq.to,
                    insert: level,
                },
            ]);

            return {
                range: range.map(changes),
                changes,
            };
        }),
        { scrollIntoView: true },
    );
};
